import {
  Button,
  ButtonGroup,
  Col,
  Container,
  Dropdown,
  Nav,
  Navbar,
  NavItem,
  Row,
} from 'react-bootstrap';
import caatLogo from 'assets/images/caatSimple.png';
// import caatGradientLogo from 'assets/images/caat-gradient-logo.png';
import userAccountSignal from 'signals/UserAccount.signal';
import SignOutButton from './SignOut';
import { COLORS, PORTAL_TYPE_HOME } from '../Constant/constants';
import { notificationDrawerSignal } from 'signals/Notification.signal';
import { notificationBellIcon, searchIcon, userIcon, timesIcon } from '../Constant/Icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import history from '../../../history';
import { onTestMode } from '../../../libs/functions/global.functions';
import {
  businessTopNav,
  edoTopNav,
  executiveTopNav,
  lenderTopNav,
  vcTopNav,
} from '../Constant/componentProps';
import { isEdoFeatureExcludeVisible } from 'libs/edoNavFeatureVisibility';
import { Link } from 'react-router-dom';
import { useRef, useState, useEffect, Fragment } from 'react';
import $appSettings from 'signals/AppSettings.signal';
import api from 'api/api';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import { handleGlobalSearch, redirectToResource } from 'libs/searchNavigation';
import { highlightSearchTerms, RECORD_FIELDS, RECORD_FIELDS_TO_RENDER, RECORD_TYPE_CATEGORY_ORDER } from '../GlobalSearchResults/globalSearch.helpers';
import { camelCaseToTitleText } from 'utils/formatTxt';
import './Header.scss';

const filterBy = () => true;

const getRecordTypeLabel = (recordType) => {
  if (recordType === 'VentureCapitalFirm') {
    return 'VC';
  }
  return camelCaseToTitleText(recordType);
};

const getCategoryRoute = (recordType) => {
  const portalType = userAccountSignal.value?.userData?.account?.portalType;

  if (portalType !== 'EDO') {
    return null; // Only EDO users get category links
  }

  // Map record types to their EDO list view routes
  const routeMap = {
    'Business': '/edo/business',
    'Lender': '/edo/lenders',
    'VentureCapitalFirm': '/edo/vc',
    'Fund': '/edo/funds',
    'FundingProgram': '/edo/programs',
    'Application': '/edo/portfolio',
    'CapitalCall': '/edo/capital-calls',
    'ProgramRequest': '/edo/program-requests',
  };

  return routeMap[recordType] || null;
};

const getRecordDetails = (option) => {
  const details = [];

  // Add ID as the first field (convert to string for consistent display)
  if (option.recordId != null) {
    details.push({ label: 'ID', value: String(option.recordId) });
  }

  if (!option.record || !option.recordType) {
    return details;
  }

  const fieldsToShow = RECORD_FIELDS_TO_RENDER[option.recordType] || [];

  fieldsToShow.forEach((field) => {
    const value = RECORD_FIELDS[field]?.(option.record, option);
    const displayValue = (value && value !== 'No description available.') ? value : '—';
    details.push({ label: field, value: displayValue });
  });

  return details;
};

const SearchResultItem = ({ option, searchQuery = '' }) => {
  const details = getRecordDetails(option);

  return (
    <div className="search-result-item">
      {details.length > 0 && (
        <div className="result-content">
          {details.map((detail, idx) => {
            const nameFields = [
              'Name',
              'Business',
              'Lender',
              'VC',
              'Funding Program',
              'Fund',
            ];
            const isNameField = nameFields.includes(detail.label);
            // Convert value to string for highlighting (handles numbers, null, etc.)
            const valueAsString = detail.value != null ? String(detail.value) : '';
            const displayValue = valueAsString.length > 80
              ? `${valueAsString.substring(0, 80)}...`
              : valueAsString;

            return (
              <div key={idx} className="content-line">
                <span className="content-label">{detail.label}:</span>
                <span
                  className={`content-value ${
                    isNameField ? 'font-weight-bold' : ''
                  }`}
                >
                  {highlightSearchTerms(displayValue, searchQuery)}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {option.content && (
        <div className="result-content">
          <div className="content-line">
            <span className="content-label">Content:</span>
            <span className="content-value">
              {highlightSearchTerms(
                option.content.length > 120
                  ? `${option.content.substring(0, 120)}...`
                  : option.content,
                searchQuery
              )}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const SEARCH_HISTORY_KEY_PREFIX = 'searchHistory_';
const MAX_HISTORY_ITEMS = 5;

// Get user-specific search history key
const getSearchHistoryKey = () => {
  const accountId = userAccountSignal.value?.userData?.accountId;
  if (!accountId) {
    return null;
  }
  return `${SEARCH_HISTORY_KEY_PREFIX}${accountId}`;
};

const SearchAutocomplete = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [searchHistory, setSearchHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [clearKey, setClearKey] = useState(0);
  const [menuMetrics, setMenuMetrics] = useState({
    width: '500px',
    minWidth: '500px',
    maxWidth: '500px',
    left: '0',
  });
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const menuRef = useRef(null);
  const saveHistoryTimeoutRef = useRef(null);
  const lastSearchQueryRef = useRef('');
  const searchDebounceRef = useRef(null);
  const searchRequestIdRef = useRef(0);
  const isClearingRef = useRef(false);
  const pendingSearchPromiseRef = useRef(null);
  const GLOBAL_SEARCH_DEBOUNCE_MS = 100;

  // Load search history from localStorage on mount and when user changes
  useEffect(() => {
    const historyKey = getSearchHistoryKey();
    if (!historyKey) {
      setSearchHistory([]);
      // Cleanup timeout on unmount
      return () => {
        if (saveHistoryTimeoutRef.current) {
          clearTimeout(saveHistoryTimeoutRef.current);
        }
      };
    }

    try {
      const stored = localStorage.getItem(historyKey);
      if (stored) {
        const history = JSON.parse(stored);
        setSearchHistory(Array.isArray(history) ? history.slice(0, MAX_HISTORY_ITEMS) : []);
      } else {
        setSearchHistory([]);
      }
    } catch (error) {
      console.error('Error loading search history:', error);
      setSearchHistory([]);
    }

    // Cleanup timeouts on unmount
    return () => {
      if (saveHistoryTimeoutRef.current) {
        clearTimeout(saveHistoryTimeoutRef.current);
      }
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [userAccountSignal.value?.userData?.accountId]);

  // Save search query to history
  const saveToHistory = (query) => {
    if (!query || query.trim().length < 3) return;

    const trimmedQuery = query.trim();
    setSearchHistory((prev) => {
      // Remove duplicate if exists, then add to front
      const filtered = prev.filter(item => item.toLowerCase() !== trimmedQuery.toLowerCase());
      const newHistory = [trimmedQuery, ...filtered].slice(0, MAX_HISTORY_ITEMS);

      // Save to localStorage with user-specific key
      const historyKey = getSearchHistoryKey();
      if (historyKey) {
        try {
          localStorage.setItem(historyKey, JSON.stringify(newHistory));
        } catch (error) {
          console.error('Error saving search history:', error);
        }
      }

      return newHistory;
    });
  };

  const handleSearch = (query) => {
    if (!query || query.trim().length < 3) {
      setOptions([]);
      setIsMenuOpen(false);
      pendingSearchPromiseRef.current = null;
      return Promise.resolve([]);
    }

    const trimmedQuery = query.trim();
    if (pendingSearchPromiseRef.current?.query === trimmedQuery) {
      return pendingSearchPromiseRef.current.promise;
    }

    setIsLoading(true);
    setSearchQuery(trimmedQuery);
    setShowHistory(false);

    const requestId = (searchRequestIdRef.current += 1);

    const promise = api
      .get({
        path: '/search',
        options: {
          searchQuery: trimmedQuery,
          limit: 100,
        },
      })
      .then((items) => {
        if (requestId !== searchRequestIdRef.current) return [];
        const results = items || [];
        setOptions(results);
        if (results.length > 0) {
          if (results.length === 1) {
            const recordType = results[0].recordType;
            if (recordType) {
              setExpandedCategories(new Set([recordType]));
            }
          } else {
            setExpandedCategories(new Set());
          }
          setIsMenuOpen(true);
        } else {
          setIsMenuOpen(false);
        }
        lastSearchQueryRef.current = trimmedQuery;
        if (saveHistoryTimeoutRef.current) {
          clearTimeout(saveHistoryTimeoutRef.current);
        }
        if (trimmedQuery.length >= 3) {
          saveHistoryTimeoutRef.current = setTimeout(() => {
            if (lastSearchQueryRef.current && lastSearchQueryRef.current.trim().length >= 3) {
              saveToHistory(lastSearchQueryRef.current);
            }
            saveHistoryTimeoutRef.current = null;
          }, 1500);
        }
        return results;
      })
      .catch((error) => {
        if (requestId !== searchRequestIdRef.current) return [];
        console.error('Search error:', error);
        setOptions([]);
        return [];
      })
      .finally(() => {
        if (requestId === searchRequestIdRef.current) {
          setIsLoading(false);
          if (pendingSearchPromiseRef.current?.query === trimmedQuery) {
            pendingSearchPromiseRef.current = null;
          }
        }
      });

    pendingSearchPromiseRef.current = { promise, query: trimmedQuery };
    return promise;
  };

  // Update menu open state when options change or history should be shown
  useEffect(() => {
    const shouldOpen = options.length > 0 || (showHistory && searchHistory.length > 0);
    setIsMenuOpen(shouldOpen);
  }, [options, showHistory, searchHistory.length]);

  useEffect(() => {
    if (!isMenuOpen && !showHistory) {
      return;
    }

    const handleClickOutside = (event) => {
      const target = event.target;

      const isInsideContainer = containerRef.current && containerRef.current.contains(target);

      const isInsideMenu = menuRef.current && menuRef.current.contains(target);

      const menuElement = document.querySelector('.rbt-menu');
      const isInsideMenuByClass = menuElement && menuElement.contains(target);

      const isRbtComponent = target.closest('#search-input') ||
                            target.closest('.rbt-input-main') ||
                            target.closest('.rbt-input') ||
                            target.closest('.rbt-menu');

      if (!isInsideContainer && !isInsideMenu && !isInsideMenuByClass && !isRbtComponent) {
        setIsMenuOpen(false);
        setShowHistory(false);
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside, true);
    }, 200);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [isMenuOpen, showHistory]);

  useEffect(() => {
    if (!isMenuOpen) return;

    const calculateMenuMetrics = () => {
      if (window.innerWidth <= 768) {
        return {
          width: 'calc(100vw - 40px)',
          minWidth: 'auto',
          maxWidth: 'calc(100vw - 40px)',
          left: '0',
        };
      }

      if (!containerRef.current) {
        return {
          width: '500px',
          minWidth: '500px',
          maxWidth: '500px',
          left: '0',
        };
      }

      const containerWidth = containerRef.current.offsetWidth;
      let leftOffset = '0';

      try {
        const inputWrapper =
          containerRef.current.querySelector('.rbt-input-main') ||
          containerRef.current.querySelector('.rbt-input-wrapper') ||
          containerRef.current.querySelector('input');

        if (inputWrapper) {
          const containerRect = containerRef.current.getBoundingClientRect();
          const inputRect = inputWrapper.getBoundingClientRect();
          leftOffset = `${containerRect.left - inputRect.left}px`;
        }
      } catch (error) {
        leftOffset = '0';
      }

      return {
        width: `${containerWidth}px`,
        minWidth: `${containerWidth}px`,
        maxWidth: '500px',
        left: leftOffset,
      };
    };

    const updateMenuMetrics = () => {
      setMenuMetrics(calculateMenuMetrics());
    };

    updateMenuMetrics();
    window.addEventListener('resize', updateMenuMetrics);

    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined' && containerRef.current) {
      resizeObserver = new ResizeObserver(updateMenuMetrics);
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateMenuMetrics);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [isMenuOpen]);

  const handleEnterKeyPress = (event) => {
    if (event.key !== 'Enter') return;

    const query = inputRef.current?.state?.text ?? '';
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    if (saveHistoryTimeoutRef.current) {
      clearTimeout(saveHistoryTimeoutRef.current);
      saveHistoryTimeoutRef.current = null;
    }
    if (query && query.trim().length >= 3) {
      saveToHistory(query);
    }

    const trimmedQuery = query?.trim() ?? '';
    if (trimmedQuery.length >= 3) {
      handleSearch(trimmedQuery).then((results) => {
        handleGlobalSearch(trimmedQuery, Array.isArray(results) && results.length > 0 ? results : undefined);
        inputRef.current?.blur();
      });
    } else {
      handleGlobalSearch(query, undefined);
      inputRef.current?.blur();
    }
  };

  const handleHistoryClick = (historyItem) => {
    // Clear any pending debounced search so we don't double-fire
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    // Clear any pending history save timeout
    if (saveHistoryTimeoutRef.current) {
      clearTimeout(saveHistoryTimeoutRef.current);
      saveHistoryTimeoutRef.current = null;
    }
    // Set the input value and trigger search
    if (inputRef.current) {
      inputRef.current.setState({ text: historyItem });
      setInputValue(historyItem);
      lastSearchQueryRef.current = historyItem;
      setShowHistory(false); // Hide history when selecting
      // Keep menu open while searching (will be updated when results arrive)
      setIsMenuOpen(true);
      // Immediately save to history when clicking a history item (move to front)
      saveToHistory(historyItem);
      // Trigger search - this will set options and maintain isMenuOpen
      handleSearch(historyItem);
    }
  };

  const handleRemoveHistoryItem = (historyItem, e) => {
    e.stopPropagation();
    e.preventDefault();

    setSearchHistory((prev) => {
      const filtered = prev.filter(item => item.toLowerCase() !== historyItem.toLowerCase());

      // Save to localStorage with user-specific key
      const historyKey = getSearchHistoryKey();
      if (historyKey) {
        try {
          localStorage.setItem(historyKey, JSON.stringify(filtered));
        } catch (error) {
          console.error('Error saving search history:', error);
        }
      }

      return filtered;
    });
  };

  const handleClearSearch = () => {
    isClearingRef.current = true;
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    if (saveHistoryTimeoutRef.current) {
      clearTimeout(saveHistoryTimeoutRef.current);
      saveHistoryTimeoutRef.current = null;
    }
    setOptions([]);
    setSearchQuery('');
    setShowHistory(false);
    setIsMenuOpen(false);
    setInputValue('');
    lastSearchQueryRef.current = '';
    setClearKey((k) => k + 1);
    if (inputRef.current) {
      inputRef.current.clear();
      inputRef.current.blur();
    }
    setTimeout(() => {
      isClearingRef.current = false;
    }, 150);
  };

  const renderMenu = (results, menuProps) => {
    // Filter out non-DOM props from menuProps
    const {
      innerRef,
      emptyLabel,
      maxHeight,
      newSelectionPrefix,
      paginationText,
      renderMenuItemChildren,
      ...validMenuProps
    } = menuProps;

    // Show history if input is focused, no results, and we have history
    const shouldShowHistory = showHistory && results.length === 0 && searchHistory.length > 0;

    const validResults = results.filter((option) => {
      return (option.recordType &&
        typeof option.recordType === 'string' &&
        option.recordType.trim() !== '' &&
        option.recordType !== 'Other' &&
        option.record && true && true);
    });

    const groups = {};
    validResults.forEach((option) => {
      const type = option.recordType;
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(option);
    });

    // Convert to flat array with category headers (same order as search-all page)
    const groupedResults = [];
    const orderedTypes = RECORD_TYPE_CATEGORY_ORDER.filter((t) => groups[t]);
    [...orderedTypes, ...Object.keys(groups).filter((t) => !RECORD_TYPE_CATEGORY_ORDER.includes(t)).sort()].forEach((recordType) => {
        // Add category header
        groupedResults.push({
          isCategoryHeader: true,
          recordType,
          count: groups[recordType].length,
          id: `category-${recordType}`,
        });
        // Only add items in this category if it's expanded
        if (expandedCategories.has(recordType)) {
          groups[recordType].forEach((item) => {
            groupedResults.push(item);
          });
        }
      });

    return (
      <div
        {...validMenuProps}
        ref={(node) => {
          innerRef(node);
          menuRef.current = node;
        }}
        className="rbt-menu"
        style={{
          width: menuMetrics.width,
          minWidth: menuMetrics.minWidth,
          maxWidth: menuMetrics.maxWidth,
          height: 'auto',
          maxHeight: '500px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          background: 'rgba(255, 255, 255, 0.98)',
          left: menuMetrics.left,
          right: 'auto',
          marginLeft: '0',
          marginRight: 'auto',
          borderRadius: '0 0 16px 16px',
          marginTop: '0',
          paddingTop: '0',
          borderTop: '1px solid rgba(255, 255, 255, 0.2)',
          ...validMenuProps.style,
        }}
      >
        <div
          style={{
            overflowY: 'auto',
            overflowX: 'hidden',
            maxHeight: '500px',
            borderRadius: '0 0 16px 16px',
          }}
        >
        {shouldShowHistory ? (
          <>
            <div className="rbt-menu-item category-header" style={{ padding: '8px 16px', fontSize: '0.7rem', color: '#6c757d' }}>
              Recent Searches
            </div>
            {searchHistory.map((historyItem, idx) => (
              <div
                key={`history-${idx}`}
                className="rbt-menu-item history-item"
                role="option"
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent input blur when clicking history item
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleHistoryClick(historyItem);
                }}
                style={{ cursor: 'pointer' }}
              >
                <div className="search-result-item history-item" style={{ padding: '8px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <div className="result-content" style={{ flex: 1, minWidth: 0 }}>
                    <div className="content-line">
                      <span className="content-value" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '0.9rem' }}>🔍</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{historyItem}</span>
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="remove-history-button"
                    onClick={(e) => handleRemoveHistoryItem(historyItem, e)}
                    aria-label={`Remove ${historyItem} from history`}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'rgba(108, 117, 125, 0.7)',
                      cursor: 'pointer',
                      padding: '4px 8px',
                      marginLeft: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      fontSize: '0.875rem'
                    }}
                  >
                    <FontAwesomeIcon icon={timesIcon} />
                  </button>
                </div>
              </div>
            ))}
          </>
        ) : groupedResults.length === 0 ? (
          <div className="rbt-menu-item">
            <div className="search-result-item" style={{ padding: '12px 16px' }}>
              <div className="result-content">
                <span className="content-value">No results found</span>
              </div>
            </div>
          </div>
        ) : (
          groupedResults.map((option, index) => {
            if (option.isCategoryHeader) {
              const isExpanded = expandedCategories.has(option.recordType);

              const handleCategoryToggle = (e) => {
                e.stopPropagation();
                e.preventDefault();
                const newExpanded = new Set(expandedCategories);
                if (isExpanded) {
                  newExpanded.delete(option.recordType);
                } else {
                  newExpanded.add(option.recordType);
                }
                setExpandedCategories(newExpanded);
                if (options.length > 0) {
                  setIsMenuOpen(true);
                }
              };

              return (
                <div
                  key={option.id}
                  className={`rbt-menu-item category-header ${isExpanded ? 'category-expanded' : 'category-collapsed'}`}
                  role="option"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={handleCategoryToggle}
                >
                  <span className="category-chevron">{isExpanded ? '▼' : '▶'}</span>
                  <span className="category-label">
                    {getRecordTypeLabel(option.recordType)} ({option.count})
                  </span>
                </div>
              );
            }

            return (
              <div
                key={option.id || `${option.recordType}-${option.recordId}-${index}`}
                className="rbt-menu-item"
                role="option"
                title="Click to view details"
                onClick={(e) => {
                  e.stopPropagation();
                  setOptions([]);
                  setShowHistory(false);
                  setInputValue('');
                  setSearchQuery('');
                  setClearKey((k) => k + 1);
                  setIsMenuOpen(false);
                  if (inputRef.current) {
                    inputRef.current.clear();
                  }
                  redirectToResource(option);
                }}
              >
                <SearchResultItem option={option} searchQuery={searchQuery} />
              </div>
            );
          })
        )}
        </div>
      </div>
    );
  };

  // Get header background color for border highlight
  const { appSettings } = $appSettings.value;
  const headerBackgroundColor =
    appSettings?.whiteLabelConfig?.headerBackgroundColor || '#29649b';

  const wrapperStyle = {
    '--header-bg-color': headerBackgroundColor,
  };

  return (
    <div
      className={`d-flex flex-row align-items-center justify-content-center search-autocomplete-wrapper ${
        isMenuOpen && (options.length > 0 || (showHistory && searchHistory.length > 0)) ? 'menu-open' : ''
      }`}
      style={wrapperStyle}
    >
      <div ref={containerRef} className="search-input-container">
        <div className="search-input-wrapper">
          <AsyncTypeahead
            key={clearKey}
            ref={inputRef}
            id={'search-input'}
            isLoading={isLoading}
            filterBy={filterBy}
            minLength={3}
            labelKey="title"
            placeholder={'Search...'}
            options={options}
            onSearch={handleSearch}
            onKeyDown={handleEnterKeyPress}
            renderMenu={renderMenu}
            open={isMenuOpen && (options.length > 0 || (showHistory && searchHistory.length > 0))}
          onFocus={() => {
            // Show history when focused and no search query
            const currentText = inputRef.current?.state?.text || '';
            if (currentText.length < 3) {
              // Clear previous results to show history
              setOptions([]);
              setShowHistory(true);
              setIsMenuOpen(true);
            } else if (options.length > 0) {
              setIsMenuOpen(true);
            }
          }}
          onBlur={(e) => {
            // Check if the blur is caused by clicking within the menu or on a category header
            const relatedTarget = e.relatedTarget || document.activeElement;
            const activeElement = document.activeElement;

            // Check if clicking on the clear button
            if (relatedTarget?.classList?.contains('search-clear-button') ||
               activeElement?.classList?.contains('search-clear-button') ||
               relatedTarget?.closest('.search-clear-button') ||
               activeElement?.closest('.search-clear-button')) {
              // Click was on clear button, close menu
              setIsMenuOpen(false);
              setShowHistory(false);
              return;
            }

            // Check if clicking on a history item
            if (relatedTarget?.classList?.contains('history-item') ||
                activeElement?.classList?.contains('history-item') ||
                relatedTarget?.closest('.history-item') ||
                activeElement?.closest('.history-item') ||
                relatedTarget?.classList?.contains('rbt-menu-item') && relatedTarget?.querySelector('.history-item') ||
                activeElement?.classList?.contains('rbt-menu-item') && activeElement?.querySelector('.history-item')) {
              // Click was on a history item, don't close menu - search is in progress
              return;
            }

            // Check if clicking on a category header
            if (relatedTarget?.classList?.contains('category-header') ||
                activeElement?.classList?.contains('category-header') ||
                relatedTarget?.closest('.category-header') ||
                activeElement?.closest('.category-header')) {
              // Click was on a category header, don't close menu
              return;
            }

            // Check if the new active element is within the menu
            if (menuRef.current && (
              menuRef.current.contains(relatedTarget) ||
              menuRef.current.contains(activeElement) ||
              activeElement?.closest('.rbt-menu')
            )) {
              // Click was within the menu, don't close it
              return;
            }

            // Delay to allow click events to fire
            setTimeout(() => {
              // Double-check that we're not clicking within the menu, on category header, or on history item
              const currentActive = document.activeElement;
              if (currentActive?.classList?.contains('category-header') ||
                  currentActive?.closest('.category-header') ||
                  currentActive?.classList?.contains('history-item') ||
                  currentActive?.closest('.history-item')) {
                return;
              }
              if (menuRef.current && (
                menuRef.current.contains(currentActive) ||
                currentActive?.closest('.rbt-menu')
              )) {
                return;
              }
              // Only close if we're not waiting for search results
              // Check if there are options or if a search is in progress
              // Also check if we just clicked on a history item (give it time to process)
              const justClickedHistory = relatedTarget?.closest('.history-item') ||
                                        activeElement?.closest('.history-item') ||
                                        relatedTarget?.querySelector('.history-item') ||
                                        activeElement?.querySelector('.history-item');

              if (options.length === 0 && !isLoading && !justClickedHistory) {
                setIsMenuOpen(false);
                setShowHistory(false);
                setOptions([]); // Clear options when clicking outside
              }
            }, 200);
          }}
          onInputChange={(text) => {
            setInputValue(text || '');
            if (isClearingRef.current) {
              setOptions([]);
              return;
            }
            // Clear any pending debounced search so only the latest query runs after delay
            if (searchDebounceRef.current) {
              clearTimeout(searchDebounceRef.current);
              searchDebounceRef.current = null;
            }
            // Show/hide history based on input length
            if (text && text.length >= 3) {
              setShowHistory(false);
              // Wait after user stops typing before running search
              searchDebounceRef.current = setTimeout(() => {
                handleSearch(text);
                searchDebounceRef.current = null;
              }, GLOBAL_SEARCH_DEBOUNCE_MS);
            } else {
              // Input is empty or < 3 chars: cancel any pending search and show history or clear
              if (text.length === 0 && searchHistory.length > 0) {
                setOptions([]);
                setShowHistory(true);
                setIsMenuOpen(true);
              } else if (text.length < 3 && text.length > 0) {
                setOptions([]);
                setShowHistory(true);
                setIsMenuOpen(true);
              }
            }
          }}
          maxResults={100}
          />
        </div>
        {inputValue && inputValue.length > 0 && (
          <span
            className="search-clear-button"
            style={{ marginRight: '24px' }}
            onClick={(e) => {
              e.stopPropagation();
              handleClearSearch();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClearSearch();
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="Clear search"
          >
            <FontAwesomeIcon
              icon={timesIcon}
              style={{ color: 'rgba(255, 255, 255, 0.7)' }}
            />
          </span>
        )}
      </div>
    </div>
  );
};

const NavBarItem = ({ navObject }) => {
  const { appSettings } = $appSettings.value;
  const headerBackgroundColor =
    appSettings?.whiteLabelConfig?.headerBackgroundColor || COLORS.PRIMARY;
  let { dropDownItems } = navObject;

  if (dropDownItems) {
    const userData = userAccountSignal.value.userData;
    dropDownItems = dropDownItems.filter((item) =>
      isEdoFeatureExcludeVisible(item, userData)
    );
  }

  return (
    <Dropdown as={ButtonGroup}>
      {navObject.title === 'Settings' ? (
        <Dropdown.Toggle
          split
          variant="dark"
          id="navbar-dropdown"
          className="px-16 hide-caret"
        >
          <span className="mr-8">
            <FontAwesomeIcon className="mr-8" icon={userIcon} />
            {userAccountSignal.value.userData?.displayName ||
              userAccountSignal.value.userData?.firstName}
          </span>
        </Dropdown.Toggle>
      ) : (
        <Dropdown.Toggle
          style={{ backgroundColor: headerBackgroundColor }}
          className={`px-16 border-0 rounded hide-caret ${
            navObject.route ? 'text-white text-decoration-none' : ''
          }`}
          {...(navObject.route
            ? { as: Link, to: navObject.route }
            : { href: null })}
        >
          <span
            className={`font-weight-bold ${navObject.route ? 'text-white text-decoration-none' : ''}`}
          >
            {navObject.title}
          </span>
        </Dropdown.Toggle>
      )}
      {!!dropDownItems && (
        <Dropdown.Menu
          className="border-0"
          style={{ backgroundColor: headerBackgroundColor }}
        >
          {dropDownItems.map((item) => (
            <Dropdown.Item
              key={item.id}
              className="top-nav-item"
              {...(item.route ? { as: Link, to: item.route } : { href: null })}
            >
              {item.route ? (
                <span className="text-decoration-none dropdown-hover">
                  {item.label}
                </span>
              ) : (
                <SignOutButton className="border-0 p-0 dropdown-hover" />
              )}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      )}
    </Dropdown>
  );
};

const Header = () => {
  const role = userAccountSignal.value.userData?.role;
  const { PORTAL_TYPES } = $appSettings.value.constants;
  const { appSettings } = $appSettings.value;
  const headerBackgroundColor = appSettings?.whiteLabelConfig
    ?.headerBackgroundColor
    ? appSettings.whiteLabelConfig.headerBackgroundColor
    : COLORS.PRIMARY;
  const logo = appSettings?.whiteLabelConfig?.logoBase64String
    ? appSettings.whiteLabelConfig.logoBase64String
    : caatLogo;
  const tagline =
    appSettings?.whiteLabelConfig?.tagline === null ||
    appSettings?.whiteLabelConfig?.tagline === undefined
      ? 'Powered by MStreetX'
      : appSettings.whiteLabelConfig.tagline;
  const taglineColor = appSettings?.whiteLabelConfig?.taglineColor
    ? appSettings.whiteLabelConfig.taglineColor
    : 'white';

  if (!role) {
    return null;
  }

  let headerTitle = '';
  let navBar = [];
  switch (userAccountSignal?.value?.userData?.account?.portalType) {
    case PORTAL_TYPES.state:
      headerTitle = 'State Portal';
      navBar = edoTopNav;
      break;
    case PORTAL_TYPES.lender:
      headerTitle = 'Capital Lender Portal';
      navBar = lenderTopNav;
      break;
    case PORTAL_TYPES.business:
      headerTitle = 'Business Portal';
      navBar = businessTopNav;
      break;
    case PORTAL_TYPES.edo:
      headerTitle = 'Economic Development Office Portal';
      navBar = edoTopNav;
      break;
    case PORTAL_TYPES.vc:
      headerTitle = 'Venture Capital';
      navBar = vcTopNav;
      break;
    case PORTAL_TYPES.executive:
      navBar = executiveTopNav;
    default:
      headerTitle = 'Welcome to CAAT';
      break;
  }

  const userData = userAccountSignal.value.userData;
  navBar = navBar.filter((item) => isEdoFeatureExcludeVisible(item, userData));

  return (
    <Navbar
      className="shadow"
      expand="lg"
      style={{ backgroundColor: headerBackgroundColor }}
    >
      <Container fluid>
        <Navbar.Brand target="#">
          <Row>
            <Col className="px-lg-0 col-auto">
              <img
                style={{ cursor: 'pointer' }}
                onClick={() =>
                  history.push(
                    PORTAL_TYPE_HOME[
                      userAccountSignal.value?.userData?.account.portalType
                    ]
                  )
                }
                src={logo}
                alt="CAAT logo"
                height="60"
              />
            </Col>
            <Col>
              <Row className="align-items-center">
                <Col xs={12} sm className="text-wrap">
                  <h5 className="mb-0 text-white">{headerTitle}</h5>
                </Col>
                {onTestMode() && (
                  <Col className="col-auto">
                    <span
                      className="bg-success rounded p-1 ml-lg-3 text-white"
                      style={{ fontSize: '16px', marginBottom: '4px' }}
                    >
                      Test mode
                    </span>
                  </Col>
                )}
              </Row>
              <p style={{ fontSize: 9, color: taglineColor }} className="mb-0">
                {tagline}
              </p>
            </Col>
          </Row>
        </Navbar.Brand>
        {window.location.hostname !== 'app.caat.io' && (
          <div className="flex-grow-1 d-flex justify-content-center">
            <SearchAutocomplete />
          </div>
        )}
        <Navbar.Toggle />
        <div className="d-flex flex-column">
          <Navbar.Collapse className="justify-content-end text-white align-items-center">
            <Nav className="navbar-nav-items w-100 justify-content-end d-flex">
              {navBar.length &&
                navBar.map((navBarItem) => (
                  <Fragment key={navBarItem.id}>
                    {navBarItem.title === 'Settings' &&
                      userAccountSignal?.value?.userData?.account?.portalType !==
                        PORTAL_TYPES.executive && (
                        <NavItem>
                          <Button
                            variant="none"
                            onClick={() =>
                              notificationDrawerSignal.update({
                                isOpen: !notificationDrawerSignal.value.isOpen,
                              })
                            }
                          >
                            <FontAwesomeIcon
                              className="text-white"
                              style={{ fontSize: 24 }}
                              icon={notificationBellIcon}
                            />
                          </Button>
                        </NavItem>
                      )}
                    <NavItem>
                      <NavBarItem navObject={navBarItem} />
                    </NavItem>
                  </Fragment>
                ))}
            </Nav>
          </Navbar.Collapse>
        </div>
      </Container>
    </Navbar>
  );
};

export default Header;
